import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';

const endpoint = 'ums/v1/cms/exclusive-deals/suppliers';
export const createDealProductEndpointIdentifier = endpoint;

// Mirrors BE CreateSupplierProductDto — the deals-scoped product create endpoint
// (POST /cms/exclusive-deals/suppliers/:supplierId/products) accepts origin + state,
// unlike the legacy catalog create route this flow used before.
export interface CreateDealProductBody {
  nameEn: string;
  code?: string;
  brandEn?: string;
  defaultUnit?: string;
  defaultPrice?: number;
  specs?: string;
  imageUrl?: string;
  origin?: string;
  state?: string;
  // Depth-3/4 catalog category, copied from the requested master product by the
  // quote slider's inline create. Ignored by BEs that predate the field.
  catalogCategoryId?: number;
}

type TData = Record<string, any>;
type TError = AxiosError;
type TVariables = { supplierId: number; body: CreateDealProductBody };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/**
 * Create a supplier-level product from the exclusive-deals grid's inline create flow.
 */
export function useCreateDealProductApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const queryClient = useQueryClient();

  const mutationFn = async ({ supplierId, body }: TVariables) => {
    try {
      const { data } = await http.post(`${endpoint}/${supplierId}/products`, body);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${endpoint}/products`],
    onSuccess: () => {
      // Refresh supplier product dropdowns so the new product appears without a page refresh.
      queryClient.invalidateQueries(['ums/v1/products']);
      toast({ description: 'New product has been created successfully', status: 'success' });
    },
    ...options,
  });
}
